import { inject, Injectable } from "@angular/core";
import { DriverDto } from "@dtos/driver.dto";
import { GooglePlaceListItemDto } from "@dtos/googlePlace";
import { TransportDto, TransportHistoryListItemDto, TransportHistoryListParamsDto, TransportAssignedListItemDto, TransportAssignedListParamsDto, PhaseEnum } from "@dtos/transports/transport.dto";
import { TransportsRetrieveResponse } from "@dtos/transports/transports.dto.old";
import { ApiRequestOptions, ApiService } from "@services/api.service";
import { EndPoints } from "@services/EndPoints";
import { Helpers } from "@utils/helpers";
import { CancelTransportDto, CancelTransportInfo } from "@dtos/quality/cancel-transport.dto";
import { TransportUnassignDriverDto, UnassignDriverPriceResponseDto } from "@dtos/transports/transport-unassign-driver.dto";
import { TransportDetailGetter } from "@dtos/transports/transport.get.dto";
import { TransportPreviewResponseDto } from "@dtos/transports/transport-preview.dto";
import { TransportPrincipalType } from "@enums/transport.enum";
import { AdminAppointmentInputDto, TransportAppointmentAdminDto } from "@dtos/transports/transport-appointment.dto";

@Injectable({ providedIn: 'root' })
export class TransportProvider {

    private readonly api = inject(ApiService);
    async loadHistory(params: TransportHistoryListParamsDto): Promise<{ list: TransportHistoryListItemDto[], totalRecords: number, pendingAppointmentsCount: number } | null> {
        try {
            params.page = params.page || 1;
            params.page_size = params.page_size || 10000000;
            const result: any = await this.api.get<any>({ url: EndPoints.getTransportHistory, queryParams: params });

            return {
                list: (result?.results ?? []).map((item: any) => TransportHistoryListItemDto.fromJson(item)),
                totalRecords: result?.count ?? 0,
                pendingAppointmentsCount: result?.pending_appointments_count ?? 0
            };

        } catch (error) {
            return null;
        }
    }

    async getDriverTransports(driverId: number): Promise<TransportAssignedListItemDto[]> {
        try {
            const result: any = await this.api.get<any>({
                url: EndPoints.getTransports,
                queryParams: { driver_id: driverId, page_size: 50 }
            });
            return (result?.results ?? []).map((item: any) => TransportAssignedListItemDto.fromJson(item));
        } catch (error) {
            console.error('Error al obtener transportes del conductor:', error);
            return [];
        }
    }

    async loadAssigned(params: TransportAssignedListParamsDto): Promise<{ list: TransportAssignedListItemDto[], totalRecords: number } | null> {
        try {
            params.page = params.page || 1;
            params.page_size = params.page_size || 10000000;
            const result: any = await this.api.get<any>({ url: EndPoints.getTransports, queryParams: params });

            return {
                list: result.results.map((item: any) => TransportAssignedListItemDto.fromJson(item)) as TransportAssignedListItemDto[],
                totalRecords: result.count
            };

        } catch (error) {
            return null;
        }
    }

    async getTransport(id: number | string): Promise<TransportDto | null> {
        try {
            const url = EndPoints.getTransport.replace("{transportId}", id.toString());
            return await this.api.get<TransportDto>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getTransportFull(id: number | string): Promise<TransportDetailGetter | null> {
        try {
            const url = EndPoints.updateTransport.replace("{transportId}", id.toString());
            return await this.api.get<TransportDetailGetter>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getTransportDocuments(id: number | string): Promise<any | null> {
        try {
            const url = EndPoints.getTransportDocuments.replace("{transportId}", id.toString());
            return await this.api.get<any>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async saveTransportDocuments(id: number | string, file_template: File, phase: PhaseEnum, typeTransport: TransportPrincipalType): Promise<any | null> {
        try {
            const url = EndPoints.saveTransportDocuments.replace("{transportId}", id.toString());
            return await this.api.post<any>({ url,  formParams: { file_template, phase, transport_principal_type: typeTransport } });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getTransportSummaryStart(id: number | string): Promise<any | null> {
        try {
            const url = EndPoints.getTransportSummaryStart.replace("{transportId}", id.toString());
            return await this.api.get<any>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getTransportSummaryEnd(id: number | string): Promise<any | null> {
        try {
            const url = EndPoints.getTransportSummaryEnd.replace("{transportId}", id.toString());
            return await this.api.get<any>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getTransportSummaryTransit(id: number | string): Promise<any | null> {
        try {
            const url = EndPoints.getTransportSummaryTransit.replace("{transportId}", id.toString());
            return await this.api.get<any>({ url });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }

    async getUserClients(): Promise<any[] | null> {
        try {

            return await this.api.get<any[]>({ url: EndPoints.getClientUsers + "?mini=true" });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getUserClientsFull(): Promise<any[] | null> {
        try {

            return await this.api.get<any[]>({ url: EndPoints.getClientUsers });
        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async billingFromUser(userId?: number): Promise<any[] | null> {
        try {
            const options: ApiRequestOptions = { url: EndPoints.getClientBillingAccounts };
            if (userId) {
                options.queryParams = { client_user: userId };

            }
            return (await this.api.get<any[]>(options)).map(billing => ({
                id: billing.id,
                clientId: billing.client,
                name: billing.business_name
            }));

        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async billingFromClient(clientId?: number): Promise<any[] | null> {
        try {
            const options: ApiRequestOptions = { url: EndPoints.getClientBillingAccounts };
            if (clientId) {
                options.queryParams = { client: clientId };

            }
            return (await this.api.get<any[]>(options)).map(billing => ({
                id: billing.id,
                clientId: billing.client,
                name: billing.entity_number.trim().length ? billing.entity_number + " - " + billing.business_name : billing.business_name,
                isFavorite: billing.is_favourite
            }));

        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async getVehicleCarfax(licence?: string): Promise<any | null> {
        try {
            return (await this.api.get<any[]>({ url: EndPoints.getVehicleCarfax.replace("{licensePlate}", licence || "") }));

        } catch (error) {
            console.error('Error al obtener transporte:', error);
            return null;
        }
    }
    async publishTransport(transport: Partial<TransportDto>): Promise<any | null> {
        try {
            var tranportToSend = transport as any;
            for (let l of tranportToSend.legs) {
                if (l.documents_arrival instanceof Array === true) {
                    for (let d of l.documents_arrival) {
                        if (d.file_template instanceof File) {
                            d.file_template = await Helpers.fileToBase64(d.file_template);
                        }
                    }
                }
                if (l.documents_departure instanceof Array === true) {

                    for (let d of l.documents_departure) {
                        if (d.file_template instanceof File) {
                            d.file_template = await Helpers.fileToBase64(d.file_template);
                        }
                    }
                }
                for (let v of l.vehicles) {
                    delete (v.insurance_date_end);
                    delete (v.insurance_date_start);
                }
            }
            return (await this.api.post<any>({ url: EndPoints.createTransport, body: tranportToSend }));

        } catch (error) {
            console.error('Error al publicar transporte:', error);
            throw error;
        }
    }
    async updateTransport(id: number, transport: Partial<TransportDto>): Promise<any | null> {
        try {
            var tranportToSend = transport as any;
            for (let l of tranportToSend.legs) {
                if (l.documents_arrival instanceof Array === true) {
                    for (let d of l.documents_arrival) {
                        if (d.file_template instanceof File) {
                            d.file_template = await Helpers.fileToBase64(d.file_template);
                        }
                    }
                }
                if (l.documents_departure instanceof Array === true) {

                    for (let d of l.documents_departure) {
                        if (d.file_template instanceof File) {
                            d.file_template = await Helpers.fileToBase64(d.file_template);
                        }
                    }
                }

            }
            return (await this.api.patch<any>({ url: EndPoints.updateTransport.replace("{transportId}", id.toString()), body: tranportToSend }));

        } catch (error) {
            console.error('Error al actualizar transporte:', error);
            throw error;
        }
    }
    async deleteTransport(transportId: number | string): Promise<any | null> {
        try {
            return (await this.api.delete<any>({ url: EndPoints.deleteTransport.replace("{transportId}", transportId.toString()) }));
        } catch (error) {
            throw error;
        }
    }
    async getAdjustmentRangeOptions(): Promise<any | null> {
        try {
            return (await this.api.get<any>({ url: EndPoints.getCancelTransportAdjustmentsThresholds }));
        } catch (error) {
            console.error('Error al obtener opciones de ajuste', error);
            return null;
        }
    }
    async pricePreview(data: any): Promise<TransportPreviewResponseDto | null> {
        try {
            const res = await this.api.post<any>({ url: EndPoints.calculatePrice, body: data, silent: true });
            return res ? TransportPreviewResponseDto.fromJson(res) : null;

        } catch (error) {
            console.error('Error al obtener transporte:', error);
           throw error;
        }
    }

    async getTransportTimeline(transportId: number | string): Promise<any | null> {
        try {
            const url = EndPoints.getTransportTimeline.replace("{id}", transportId.toString());
            return await this.api.get<any>({ url });
        } catch (error) {
            console.error('Error al obtener timeline del transporte:', error);
            return null;
        }
    }

    async getCancelTransportAdjustmentsThresholds(): Promise<any | null> {
        try {
            return await this.api.get<any>({ url: EndPoints.getCancelTransportAdjustmentsThresholds });
        } catch (error) {
            console.error('Error al obtener ajustes de umbrales para cancelar transporte:', error);
            return null;
        }
    }

    async cancelTransport(transportId: number | string, cancelData: CancelTransportDto): Promise<any | null> {
        try {
            const url = EndPoints.cancelTransport.replace("{transportId}", transportId.toString());
            return await this.api.post<any>({ url, body: cancelData });
        } catch (error) {
            console.error('Error al cancelar transporte (POST):', error);
            throw error;
        }
    }

    async updateCancelTransport(transportId: number | string, cancelData: CancelTransportDto): Promise<any | null> {
        try {
            const url = EndPoints.cancelTransport.replace("{transportId}", transportId.toString());
            return await this.api.put<any>({ url, body: cancelData });
        } catch (error) {
            console.error('Error al actualizar cancelación de transporte (PUT):', error);
            throw error;
        }
    }

    async getCancelTransport(transportId: number | string): Promise<CancelTransportInfo | null> {
        try {
            const url = EndPoints.getCancelTransportInfo.replace("{transportId}", transportId.toString());
            const result = await this.api.get<any>({ url });
            return result ? CancelTransportInfo.fromJson(result) : null;
        } catch (error) {
            console.error('Error al obtener información de cancelación:', error);
            // return null; // O lanzar error según convenga, por ahora null para no romper
            return null;
        }
    }

    async unassignDriver(unassignData: TransportUnassignDriverDto): Promise<any | null> {
        try {
            const url = EndPoints.unassignDriver.replace("{unnasignId}/", "");
            return await this.api.post<any>({ url, body: unassignData.toPost() });
        } catch (error) {
            console.error('Error al desasignar conductor:', error);
            throw error;
        }
    }
    async updateUnassignDriver(unassignData: TransportUnassignDriverDto): Promise<any | null> {
        try {
            const url = EndPoints.unassignDriver.replace("{unnasignId}", unassignData.id!.toString());
            delete unassignData.id;
            return await this.api.patch<any>({ url, body: unassignData.toPatch() });
        } catch (error) {
            console.error('Error al desasignar conductor:', error);
            throw error;
        }
    }
    async getUnassignDriver(unnasignId: number | string): Promise<TransportUnassignDriverDto | null> {
        try {
            const url = EndPoints.unassignDriver.replace("{unnasignId}", unnasignId.toString());
            const result = await this.api.get<any>({ url });
            if(!result){
                throw new Error("No se encontro la desasignacion");
            }
            return TransportUnassignDriverDto.fromUnassignedDriverView(result);
        } catch (error) {
            console.error('Error al obtener información de desasignación:', error);
            // return null; // O lanzar error según convenga, por ahora null para no romper
            throw error;
        }
    }
    async getTransporUnasingDriverPrices(transportId: number | string): Promise<UnassignDriverPriceResponseDto> {
      try {
        const url = EndPoints.unassignDriverPrice.replace("{transportId}", transportId.toString());
        const result = await this.api.get<UnassignDriverPriceResponseDto>({ url });
        if(!result){
            throw new Error("No se encontro la desasignacion");
        }
        return UnassignDriverPriceResponseDto.fromJson(result);
    } catch (error) {
        console.error('Error al obtener información de desasignación:', error);
        // return null; // O lanzar error según convenga, por ahora null para no romper
        throw error;
    }
    }    

    /**
     * Obtiene las respuestas de la encuesta para un transporte y fase determinados
     */
    async getSurveyAnswers(transportId: number | string, phase: 'DEPARTURE' | 'ARRIVAL'): Promise<any[]> {
        try {
            const url = EndPoints.getTransportSurveyAnswers
                .replace('{transportId}', transportId.toString())
                .replace('{phase}', phase);
            const result = await this.api.get<any[]>({ url });
            return result || [];
        } catch (error) {
            console.error(`Error al obtener respuestas de encuesta para la fase ${phase}:`, error);
            return [];
        }
    }
    /**
     * Obtiene los datos de cita de un transporte para el modal de administración
     */
    async getAppointment(transportId: number | string): Promise<TransportAppointmentAdminDto | null> {
        try {
            const url = EndPoints.getTransportAppointment.replace("{transportId}", transportId.toString());
            const result = await this.api.get<any>({ url });
            return result ? TransportAppointmentAdminDto.fromJson(result) : null;
        } catch (error) {
            console.error('Error al obtener la cita del transporte:', error);
            return null;
        }
    }

    /**
     * Fija por primera vez la cita de un transporte (POST)
     */
    async bookAppointment(transportId: number | string, data: AdminAppointmentInputDto): Promise<{ success: boolean; data?: TransportAppointmentAdminDto; conflict?: boolean; error?: string }> {
        try {
            const url = EndPoints.bookTransportAppointment.replace("{transportId}", transportId.toString());
            const result = await this.api.post<any>({ url, body: data });
            return {
                success: true,
                data: result ? TransportAppointmentAdminDto.fromJson(result) : undefined
            };
        } catch (error: any) {
            const isConflict = error?.status === 409 || error?.statusCode === 409;
            const errorMessage = this.extractErrorMessage(error);
            return {
                success: false,
                conflict: isConflict,
                error: errorMessage || undefined
            };
        }
    }

    /**
     * Actualiza una cita existente (PATCH). Responde 409 si el transporte ya tiene conductor asignado.
     */
    async updateAppointment(transportId: number | string, data: Partial<AdminAppointmentInputDto>): Promise<{ success: boolean; data?: TransportAppointmentAdminDto; conflict?: boolean; error?: string }> {
        try {
            const url = EndPoints.updateTransportAppointment.replace("{transportId}", transportId.toString());
            const result = await this.api.patch<any>({ url, body: data });
            return {
                success: true,
                data: result ? TransportAppointmentAdminDto.fromJson(result) : undefined
            };
        } catch (error: any) {
            const isConflict = error?.status === 409 || error?.statusCode === 409;
            const errorMessage = this.extractErrorMessage(error);
            return {
                success: false,
                conflict: isConflict,
                error: errorMessage || undefined
            };
        }
    }

    /**
     * Reenvía el correo de citación al cliente final
     */
    async resendAppointmentEmail(transportId: number | string): Promise<{ success: boolean; emailSentAt?: string; error?: string }> {
        try {
            const url = EndPoints.resendTransportAppointmentEmail.replace("{transportId}", transportId.toString());
            const result: any = await this.api.post<any>({ url });
            return {
                success: true,
                emailSentAt: result?.email_sent_at
            };
        } catch (error: any) {
            const errorMessage = this.extractErrorMessage(error);
            return {
                success: false,
                error: errorMessage || undefined
            };
        }
    }

    /**
     * Extrae un mensaje de error legible desde la respuesta HTTP del backend (Django / DRF).
     * Si no existe mensaje explicito del backend, retorna null para delegar en el servicio i18n del cliente.
     */
    private extractErrorMessage(error: any): string | null {
        if (!error) return null;

        // 1. Mensaje explicito de HttpError de Django ({ message: "..." })
        if (typeof error?.error?.message === 'string' && error.error.message.trim().length > 0) {
            return error.error.message;
        }

        // 2. Detalle de excepción DRF ({ detail: "..." })
        if (typeof error?.error?.detail === 'string' && error.error.detail.trim().length > 0) {
            return error.error.detail;
        }

        // 3. Mapa de errores de validación de campos DRF ({ field_name: ["Error..."], ... })
        if (error?.error && typeof error.error === 'object' && !Array.isArray(error.error)) {
            const fieldErrors = [];
            for (const key of Object.keys(error.error)) {
                const val = error.error[key];
                if (Array.isArray(val) && val.length > 0) {
                    fieldErrors.push(key + ': ' + val.join(', '));
                } else if (typeof val === 'string') {
                    fieldErrors.push(key + ': ' + val);
                }
            }
            if (fieldErrors.length > 0) {
                return fieldErrors.join(' | ');
            }
        }

        return null;
    }
}
